import { Suspense } from "react";
import { lazyWithRecovery } from "../lib/chunk-load-recovery/lazy-with-recovery";

import type { RouteObject } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { ProfileMigrationPage } from "../app/pages/profile/ProfileMigrationPage/ProfileMigrationPage";
import { createAuthLoader } from "../app/router/guards";
import { AppStateGuard } from "../app/router/guards/components/app-state-guard";
import { AuthGuardWrapper } from "../app/router/guards/components/auth-guard-wrapper";
import { NextUrlRedirectGuard } from "../app/router/guards/components/NextUrlRedirectGuard";
import { BaseLayout } from "../components/layouts/BaseLayout";
import DefaultLayout from "../components/layouts/default-layout/DefaultLayout";
import RouteWrapper from "../components/layouts/route-wrapper/RouteWrapper";
import {
  EXTERNAL_LOGIN_PATH,
  MOBILE_ONBOARDING_INFO_SCREEN_PATH,
  MOBILE_ONBOARDING_PATH,
  SURVEY_RELATIVE_PATH,
  VERIFY_EMAIL_PATH,
} from "../constants/env";
import { RouteWrapperVariant } from "../constants/layouts/route-wrapper-variant.enum";
import BonusXLoading from "../legacy/components/BonusXLoading";
import Header from "../legacy/components/Header";
import { DialogHandler } from "../legacy/components/poppers/DialogHandler";
import SEO from "../legacy/components/Seo";
import { LoginErrorModal } from "../legacy/pages/Auth/LoginErrorModal";
import { createProtectedCitizenRoutes } from "./protected-routes/create-protected-citizen-routes";
import { withSuspense } from "./protected-routes/utils/with-suspense";
import { createPublicRoutes } from "./public-routes";

const PAGE_TITLE = "Scopri e richiedi i BonusX te | I tuoi bonus. Il tuo tempo. I tuoi diritti.";

const EmailVerificationSentPage = lazyWithRecovery(
  () => import(/* webpackPreload: true */ "../legacy/pages/Auth/EmailVerificationSentPage")
);
const VerifyEmailPage = lazyWithRecovery(() => import("../legacy/pages/Auth/VerifyEmailPage"));
const Logout = lazyWithRecovery(() => import("./protected-routes/logout-routes"));
const ExternalLoginGuard = lazyWithRecovery(() => import("../app/router/guards/components/ExternalLoginGuard"));
const MobileOnboardingGuard = lazyWithRecovery(() => import("../app/router/guards/components/MobileOnboardingGuard"));
const SurveyByType = lazyWithRecovery(() => import("../legacy/pages/survey/survey-by-type"));

export function createAppRoutes(): RouteObject[] {
  return [
    {
      path: EXTERNAL_LOGIN_PATH,
      element: withSuspense(
        <ExternalLoginGuard>
          <BaseLayout header={<Header showLogo={true} />}>
            <BonusXLoading />
          </BaseLayout>
        </ExternalLoginGuard>
      ),
      errorElement: (
        <BaseLayout header={<Header showLogo={true} />}>
          <LoginErrorModal />
        </BaseLayout>
      ),
    },
    {
      path: MOBILE_ONBOARDING_PATH,
      element: withSuspense(<MobileOnboardingGuard />),
      errorElement: (
        <BaseLayout header={<Header showLogo={true} />}>
          <LoginErrorModal />
        </BaseLayout>
      ),
    },
    {
      path: MOBILE_ONBOARDING_INFO_SCREEN_PATH,
      element: withSuspense(<MobileOnboardingGuard servesIntro />),
      errorElement: (
        <BaseLayout header={<Header showLogo={true} />}>
          <LoginErrorModal />
        </BaseLayout>
      ),
    },
    {
      path: VERIFY_EMAIL_PATH,
      element: (
        <>
          <SEO title="Verifica email" />
          <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRoute}>
            {withSuspense(<VerifyEmailPage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      element: <AppStateGuard />,
      children: [
        {
          path: "email-verification-sent",
          element: (
            <DefaultLayout>
              <SEO title="Invio verifica email" />
              <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRoute}>
                {withSuspense(<EmailVerificationSentPage />)}
              </RouteWrapper>
            </DefaultLayout>
          ),
        },
        {
          path: "logout",
          element: withSuspense(<Logout />),
        },
        {
          path: "migrate-profile",
          element: (
            <AuthGuardWrapper>
              <DefaultLayout>
                <RouteWrapper variant={RouteWrapperVariant.fullScreen}>
                  <ProfileMigrationPage variant="white" />
                </RouteWrapper>
              </DefaultLayout>
            </AuthGuardWrapper>
          ),
        },
        {
          path: SURVEY_RELATIVE_PATH,
          loader: createAuthLoader(),
          element: (
            <AuthGuardWrapper>
              <Suspense fallback={<BonusXLoading />}>
                <SurveyByType />
              </Suspense>
            </AuthGuardWrapper>
          ),
        },
        {
          element: (
            <NextUrlRedirectGuard>
              <Outlet />
            </NextUrlRedirectGuard>
          ),
          children: [
            ...createPublicRoutes(),
            {
              element: (
                <AuthGuardWrapper>
                  <DialogHandler />
                  <SEO title={PAGE_TITLE} />
                  <Suspense fallback={<BonusXLoading />}>
                    <Outlet />
                  </Suspense>
                </AuthGuardWrapper>
              ),
              children: createProtectedCitizenRoutes(),
            },
          ],
        },
      ],
    },
  ];
}
